import type { CallDirection, CallLifecycleRequest, CallState } from '@kunzite/shared-types';

import type { AuditClient } from '../services/audit-client';
import type { CallsApiClient } from '../services/calls-api';
import type { WrappedSession } from '../sip/ua';
import type { NotifyCallInput, NotifyDialErrorInput } from '../zoho/phonebridge-client';

import type { MultiLegOrchestrator } from './multi-leg-orchestrator';

interface PhoneBridgeLike {
  notifyCall: (input: NotifyCallInput) => Promise<void>;
  notifyDialError: (input: NotifyDialErrorInput) => Promise<void>;
}

interface StoreLike {
  setIncomingCall: (input: { callId: string; remote: string }) => void;
  setOutgoingCall: (input: { callId: string; remote: string }) => void;
  markAnswered: () => void;
  markHold: () => void;
  markUnhold: () => void;
  markMuted: (muted: boolean) => void;
  markEnded: () => void;
  openWrapUp: (callId: string, mode: 'create' | 'edit') => void;
}

/**
 * Subset of CallsApiClient the orchestrator needs. Optional in deps for
 * back-compat with tests/codepaths constructed before F2-G4 (e.g., the
 * existing session.test.ts fakes a 4-method orch without `calls`).
 */
interface CallsApiLike {
  reportLifecycle: (input: CallLifecycleRequest) => Promise<void>;
}

/**
 * F4.5-D2: subset of AuditClient the orchestrator needs. Optional so legacy
 * tests can construct an orchestrator without wiring audit emission.
 */
interface AuditClientLike {
  record: (input: {
    category: 'admin' | 'oauth';
    action: string;
    actorZohouser: string | null;
    target?: Record<string, unknown>;
    before?: Record<string, unknown>;
    after?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
  }) => Promise<void>;
}

export interface CallSessionOrchestratorDeps {
  phonebridge: PhoneBridgeLike;
  store: StoreLike;
  sipExtension: string;
  sipDomain: string;
  zohouser: string;
  /** F2-G4: when present, lifecycle transitions are reported to IS as well. */
  callsApi?: CallsApiClient | CallsApiLike;
  /** F4.2-B5: optional for back-compat; required when warm transfer is used. */
  multiLeg?: MultiLegOrchestrator;
  /** F4.5-D3: best-effort audit emission for conference start/end. Optional for back-compat with legacy tests. */
  auditClient?: AuditClient | AuditClientLike;
}

export class CallSessionOrchestrator {
  private currentSession: WrappedSession | null = null;
  private currentCallId: string | null = null;
  private currentRemote: string | null = null;
  private currentDirection: CallDirection | null = null;

  constructor(private readonly deps: CallSessionOrchestratorDeps) {}

  async onIncoming(input: { callId: string; from: string; session: WrappedSession }): Promise<void> {
    this.currentSession = input.session;
    this.currentCallId = input.callId;
    this.currentRemote = input.from;
    this.currentDirection = 'inbound';
    this.deps.store.setIncomingCall({ callId: input.callId, remote: input.from });
    await this.safeNotifyPhoneBridge({
      type: 'received',
      state: 'ringing',
      id: input.callId,
      from: input.from,
      to: this.deps.sipExtension,
      zohouser: this.deps.zohouser,
    });
    await this.reportLifecycle({
      callId: input.callId,
      direction: 'inbound',
      state: 'ringing',
      fromNumber: input.from,
      toNumber: this.deps.sipExtension,
      timestampMs: Date.now(),
    });
  }

  async onOutgoingProgress(input: { callId: string; to: string; session: WrappedSession }): Promise<void> {
    // F4.5.1: detect consult leg by presence of an existing primary session.
    // The primary's currentSession is set on first dial and only cleared by
    // clear() at primary-end, so a non-null currentSession here means this
    // outgoing event is for a consult call (initiated by multiLeg.startConsult).
    // Without this guard the consult overwrote the primary's slot in both
    // session-internal state and the store's `call` field, making
    // TransferPanel render "Cliente (A) — <B's number>" after a
    // drop-then-reconsult cycle (discovered live in F4.4 acceptance).
    // PhoneBridge + lifecycle still fire below for both legs.
    const isConsultLeg = this.currentSession !== null;
    if (!isConsultLeg) {
      this.currentSession = input.session;
      this.currentCallId = input.callId;
      this.currentRemote = input.to;
      this.currentDirection = 'outbound';
      this.deps.store.setOutgoingCall({ callId: input.callId, remote: input.to });
    }
    await this.safeNotifyPhoneBridge({
      type: 'dialled',
      state: 'ringing',
      id: input.callId,
      from: this.deps.sipExtension,
      to: input.to,
      zohouser: this.deps.zohouser,
    });
    await this.reportLifecycle({
      callId: input.callId,
      direction: 'outbound',
      state: 'ringing',
      fromNumber: this.deps.sipExtension,
      toNumber: input.to,
      timestampMs: Date.now(),
    });
  }

  async onAnswered(input: { callId: string }): Promise<void> {
    if (!this.currentDirection || !this.currentRemote) return;
    this.deps.store.markAnswered();
    const fromNumber =
      this.currentDirection === 'inbound' ? this.currentRemote : this.deps.sipExtension;
    const toNumber =
      this.currentDirection === 'inbound' ? this.deps.sipExtension : this.currentRemote;
    await this.safeNotifyPhoneBridge({
      type: this.currentDirection === 'inbound' ? 'received' : 'dialled',
      state: 'answered',
      id: input.callId,
      from: fromNumber,
      to: toNumber,
      zohouser: this.deps.zohouser,
    });
    await this.reportLifecycle({
      callId: input.callId,
      direction: this.currentDirection,
      state: 'answered',
      fromNumber,
      toNumber,
      timestampMs: Date.now(),
    });
  }

  async onEnded(input: { callId: string; cause: string }): Promise<void> {
    // F4.4-C2: route call-end events through the multi-leg state machine first.
    // - During `consulting`: a consult-leg drop routes to onConsultFailed (F4.2-cleanup #4).
    // - During `conferencing`: drop the corresponding leg (primary or consult).
    //   The other leg becomes the active 1:1 call.
    const which = this.deps.multiLeg?.whichLegByCallId(input.callId);
    const multiLegState = this.deps.multiLeg?.getState();
    if (which === 'consult') {
      if (multiLegState?.kind === 'conferencing') {
        await this.deps.multiLeg!.dropParticipant('consult');
        return;
      }
      // consulting state — preserve F4.2-cleanup #4 behavior
      this.deps.multiLeg!.onConsultFailed(input.cause);
      return;
    }
    if (which === 'primary' && multiLegState?.kind === 'conferencing') {
      // F4.4-C2 INVARIANT: mirror the dropConferenceParticipant('primary') logic to
      // keep currentSession/currentCallId/currentRemote in sync with the new primary.
      // Capture the consult fields BEFORE the await, since dropParticipant transitions
      // the state synchronously and the consult fields would be lost after.
      // (See also dropConferenceParticipant: same invariant in different ordering —
      // both safe because the consult fields are read from a pre-await snapshot.)
      this.currentSession = multiLegState.consult;
      this.currentCallId = multiLegState.consultCallId;
      this.currentRemote = multiLegState.consultTarget;
      await this.deps.multiLeg!.dropParticipant('primary');
      return;
    }
    if (!this.currentDirection || !this.currentRemote) return;
    const direction = this.currentDirection;
    const fromNumber =
      direction === 'inbound' ? this.currentRemote : this.deps.sipExtension;
    const toNumber = direction === 'inbound' ? this.deps.sipExtension : this.currentRemote;
    const lifecycleState: CallState =
      input.cause === 'no-answer' || input.cause === 'rejected' || input.cause === 'cancel'
        ? 'missed'
        : 'completed';
    this.deps.store.markEnded();
    await this.safeNotifyPhoneBridge({
      type: direction === 'inbound' ? 'received' : 'dialled',
      state: lifecycleState === 'missed' ? 'missed' : 'completed',
      id: input.callId,
      from: fromNumber,
      to: toNumber,
      zohouser: this.deps.zohouser,
    });
    await this.reportLifecycle({
      callId: input.callId,
      direction,
      state: lifecycleState,
      fromNumber,
      toNumber,
      timestampMs: Date.now(),
    });
    // F2-G4: trigger the wrap-up panel right after ended, so the agent can
    // pick a disposition + write notes immediately. The panel reads from the
    // store and is gated behind state.wrapUp.open in the chrome shell.
    this.deps.store.openWrapUp(input.callId, 'create');
    this.clear();
  }

  acceptIncoming(): void {
    this.currentSession?.answer();
  }

  async rejectIncoming(): Promise<void> {
    this.currentSession?.hangup();
    if (this.currentCallId && this.currentRemote && this.currentDirection === 'inbound') {
      const callId = this.currentCallId;
      const remote = this.currentRemote;
      await this.safeNotifyPhoneBridge({
        type: 'received',
        state: 'missed',
        id: callId,
        from: remote,
        to: this.deps.sipExtension,
        zohouser: this.deps.zohouser,
      });
      await this.reportLifecycle({
        callId,
        direction: 'inbound',
        state: 'missed',
        fromNumber: remote,
        toNumber: this.deps.sipExtension,
        timestampMs: Date.now(),
      });
    }
    this.clear();
  }

  async blindTransfer(target: string): Promise<void> {
    if (!this.currentSession || !this.currentCallId) {
      throw new Error('blindTransfer: no active call');
    }
    const callId = this.currentCallId;
    const direction = this.currentDirection ?? 'outbound';
    await this.currentSession.refer(target, { domain: this.deps.sipDomain });
    // SBC accepted REFER — agent leg will end via SBC-initiated BYE.
    // Eagerly emit lifecycle "completed" + PhoneBridge "completed" so the IS
    // and Zoho see the close immediately rather than waiting for SIP BYE.
    await this.reportLifecycle({
      callId,
      direction,
      state: 'completed',
      fromNumber: this.deps.sipExtension,
      toNumber: target,
      timestampMs: Date.now(),
    });
    await this.safeNotifyPhoneBridge({
      type: direction === 'outbound' ? 'dialled' : 'received',
      state: 'completed',
      id: callId,
      from: this.deps.sipExtension,
      to: target,
      zohouser: this.deps.zohouser,
    });
    this.deps.store.markEnded();
    this.clear();
  }

  startConsult(target: string): void {
    if (!this.deps.multiLeg) throw new Error('startConsult: multiLeg orchestrator not configured');
    if (!this.currentSession || !this.currentCallId) throw new Error('startConsult: no active call');
    // Make sure the multi-leg orchestrator knows the current primary.
    this.deps.multiLeg.setPrimary({ session: this.currentSession, callId: this.currentCallId });
    this.deps.multiLeg.startConsult(target);
  }

  async cancelConsult(): Promise<void> {
    if (!this.deps.multiLeg) return;
    await this.deps.multiLeg.cancelConsult();
  }

  async completeWarmTransfer(): Promise<void> {
    if (!this.deps.multiLeg) throw new Error('completeWarmTransfer: multiLeg orchestrator not configured');
    if (!this.currentCallId) throw new Error('completeWarmTransfer: no primary callId');
    const callId = this.currentCallId;
    const direction = this.currentDirection ?? 'outbound';
    await this.deps.multiLeg.completeWarmTransfer();
    // Both agent legs ended via SBC; eagerly emit lifecycle/PhoneBridge for the primary call.
    await this.reportLifecycle({
      callId,
      direction,
      state: 'completed',
      fromNumber: this.deps.sipExtension,
      toNumber: this.currentRemote ?? '',
      timestampMs: Date.now(),
    });
    await this.safeNotifyPhoneBridge({
      type: direction === 'outbound' ? 'dialled' : 'received',
      state: 'completed',
      id: callId,
      from: this.deps.sipExtension,
      to: this.currentRemote ?? '',
      zohouser: this.deps.zohouser,
    });
    this.deps.store.markEnded();
    this.clear();
  }

  hangup(): void {
    this.currentSession?.hangup();
  }

  setMuted(muted: boolean): void {
    this.currentSession?.setMuted(muted);
    this.deps.store.markMuted(muted);
  }

  setHold(hold: boolean): void {
    this.currentSession?.setHold(hold);
    if (hold) this.deps.store.markHold();
    else this.deps.store.markUnhold();
  }

  sendDtmf(d: string): void {
    this.currentSession?.sendDtmf(d);
  }

  /**
   * Best-effort lifecycle report to IS. Failures are swallowed to avoid
   * blocking the SIP-driven orchestrator pipeline; the IS endpoint is
   * idempotent (upsert keyed by callId) so a later transition can recover
   * the missing rows.
   */
  private async reportLifecycle(payload: CallLifecycleRequest): Promise<void> {
    if (!this.deps.callsApi) return;
    try {
      await this.deps.callsApi.reportLifecycle(payload);
    } catch {
      // intentionally swallow — non-blocking, telemetry-grade signal
    }
  }

  /**
   * Best-effort wrap around PhoneBridge.notifyCall. Failures must not
   * cascade and break the lifecycle/wrap-up pipeline (best-effort,
   * non-blocking telemetry signal).
   */
  private async safeNotifyPhoneBridge(input: NotifyCallInput): Promise<void> {
    try {
      await this.deps.phonebridge.notifyCall(input);
    } catch (err) {
      console.warn('[orch] phonebridge.notifyCall failed (best-effort)', err);
    }
  }

  /**
   * F4.4-C1: convert the active consult into a 3-way conference.
   * Delegates to multiLeg.addToConference() (which un-holds the primary leg).
   * F4.5-D3: emits audit event 'conference.started' best-effort via auditClient.
   */
  async addToConference(): Promise<void> {
    if (!this.deps.multiLeg) throw new Error('addToConference: multiLeg orchestrator not configured');
    // F4.5-D3 INVARIANT: capture state BEFORE multiLeg.addToConference(). The
    // transition to 'conferencing' is synchronous, but reading after couples the
    // emission to internals — keep the audit payload pinned to the pre-transition
    // snapshot for clarity. Same pattern as F4.4-C1/C2 dropParticipant flows.
    const stateBefore = this.deps.multiLeg.getState();
    const primaryCallId = stateBefore.kind === 'consulting' ? stateBefore.primaryCallId : this.currentCallId;
    const consultCallId = stateBefore.kind === 'consulting' ? stateBefore.consultCallId : undefined;
    this.deps.multiLeg.addToConference();
    // F4.5-D3: real audit emission, replaces F4.4-C3 TODO marker.
    await this.deps.auditClient?.record({
      category: 'admin',
      action: 'conference.started',
      actorZohouser: null,
      target: {
        kind: 'conference',
        ...(primaryCallId ? { primaryCallId } : {}),
        ...(consultCallId ? { consultCallId } : {}),
      },
    });
    // F4.4-C1 deviation from spec §3.3: spec called for
    // `this.deps.store.setTransferState('conferencing')` here, but the multiLeg
    // onStateChange callback (subscribed in app.tsx via services.orch wiring)
    // already pushes the conferencing state to the store. Calling it here
    // would be redundant / risk double-write race.
  }

  /**
   * F4.4-C1: end the active 3-way conference. Hangs up both legs (via
   * multiLeg.endConference), emits lifecycle/PhoneBridge 'completed' for
   * the primary call, marks the store ended, and clears orchestrator state.
   * F4.5-D3: emits audit event 'conference.ended' best-effort via auditClient.
   */
  async endConference(): Promise<void> {
    if (!this.deps.multiLeg) return;
    if (!this.currentCallId || !this.currentDirection || !this.currentRemote) return;
    const callId = this.currentCallId;
    const direction = this.currentDirection;
    const fromNumber = direction === 'inbound' ? this.currentRemote : this.deps.sipExtension;
    const toNumber = direction === 'inbound' ? this.deps.sipExtension : this.currentRemote;
    // F4.5-D3 INVARIANT: capture consultCallId BEFORE multiLeg.endConference().
    // endConference transitions to 'idle' synchronously, dropping the consult
    // fields. Same pattern as F4.4-C2 onEnded primary-drop branch.
    const stateBefore = this.deps.multiLeg.getState();
    const consultCallId = stateBefore.kind === 'conferencing' ? stateBefore.consultCallId : undefined;
    await this.deps.multiLeg.endConference();
    await this.reportLifecycle({
      callId,
      direction,
      state: 'completed',
      fromNumber,
      toNumber,
      timestampMs: Date.now(),
    });
    await this.safeNotifyPhoneBridge({
      type: direction === 'inbound' ? 'received' : 'dialled',
      state: 'completed',
      id: callId,
      from: fromNumber,
      to: toNumber,
      zohouser: this.deps.zohouser,
    });
    this.deps.store.markEnded();
    // F4.5-D3: real audit emission, replaces F4.4-C3 TODO marker.
    await this.deps.auditClient?.record({
      category: 'admin',
      action: 'conference.ended',
      actorZohouser: null,
      target: {
        kind: 'conference',
        primaryCallId: callId,
        ...(consultCallId ? { consultCallId } : {}),
      },
    });
    // F4.4-C1 deviation from spec §3.3: setTransferState('idle') is pushed via
    // multiLeg.onStateChange, not directly here. See addToConference for rationale.
    this.clear();
  }

  /**
   * F4.4-C1: remove one participant from the 3-way conference. The other leg
   * becomes the active 1:1 call.
   * If 'primary' is dropped, the former consult becomes the new primary —
   * update currentCallId/currentRemote/currentSession to reflect the new
   * primary so subsequent operations (mute, hold, hangup) target the right
   * session.
   */
  async dropConferenceParticipant(which: 'primary' | 'consult'): Promise<void> {
    if (!this.deps.multiLeg) return;
    // F4.4-C1 deviation from spec §3.3: setTransferState('idle') is pushed via
    // multiLeg.onStateChange, not directly here. See addToConference for rationale.
    //
    // F4.4-C1 INVARIANT: capture state BEFORE the await. dropParticipant
    // synchronously transitions multiLeg from 'conferencing' to 'primary-active',
    // so reading getState() after the await would lose the consult fields we
    // need to swap currentSession/CallId/Remote on primary-drop.
    // (See also onEnded primary-drop branch: same invariant, before-await update
    // ordering — both safe because consult fields come from a pre-await snapshot.)
    const stateBefore = this.deps.multiLeg.getState();
    await this.deps.multiLeg.dropParticipant(which);
    if (which === 'primary' && stateBefore.kind === 'conferencing') {
      // The ex-consult is now the primary in the orchestrator state. Update
      // currentSession/currentCallId/currentRemote accordingly so this
      // orchestrator's operations (mute, hold, hangup) target the right session.
      this.currentSession = stateBefore.consult;
      this.currentCallId = stateBefore.consultCallId;
      this.currentRemote = stateBefore.consultTarget;
      // Direction stays as it was; the call continues as if it had been
      // dialed/received against the new remote.
    }
  }

  private clear(): void {
    this.currentSession = null;
    this.currentCallId = null;
    this.currentRemote = null;
    this.currentDirection = null;
  }
}
